import React, { createContext, useContext, useMemo } from "react";
import { FinalFormBoundaryProps } from "../types";

type FinalFormBoundaryContextData = {
  clearRateField: () => void;
  changeRateAmountField: (newRateAmount: string) => void;
};

const FinalFormBoundaryContext =
  createContext<FinalFormBoundaryContextData | null>(null);

/**
 * The intermediary between react-final-form's `FormApi` and the charge form:
 * the only part of the form that calls `formApi.change`, and everything else
 * writes values through its named operations. See the
 * `charge-form-editor-providers` spec.
 */
export const FinalFormBoundary: React.FC<FinalFormBoundaryProps> = (props) => {
  const { children, formApi } = props;

  const clearRateField = () => {
    formApi.change("rate", null);
  };

  const changeRateAmountField = (newRateAmount: string) => {
    formApi.change("rateAmount", newRateAmount);
  };

  const ctx = useMemo(
    () => ({
      clearRateField,
      changeRateAmountField,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <FinalFormBoundaryContext.Provider value={ctx}>
      {children}
    </FinalFormBoundaryContext.Provider>
  );
};

const useFinalFormBoundaryContext = (
  fn: string
): FinalFormBoundaryContextData => {
  const context = useContext(FinalFormBoundaryContext);

  if (!context) {
    throw new Error(`Cannot call ${fn} outside of a FinalFormBoundary wrapper`);
  }

  return context;
};

export const useFinalFormBoundary = (): FinalFormBoundaryContextData => {
  const { clearRateField, changeRateAmountField } = useFinalFormBoundaryContext(
    "useFinalFormBoundary"
  );

  return {
    clearRateField,
    changeRateAmountField,
  };
};
